import { Injectable, inject } from '@angular/core';
import { Observable, catchError, map, merge, of, shareReplay, tap } from 'rxjs';
import { CrudBasicDomainsService } from 'src/app/admin/services/crud-basic-domains.service';
import { RegionsService } from 'src/app/admin/services/regions.service';
import { ActionsService } from '../../risk-setup/service/actions.service';
import { NameCatalog, needsActionCatalog } from '../domain/plan-presentation';
import { PlanView } from './plan-management.models';

type Names = ReadonlyMap<string, string>;

/**
 * Resolves the names a PlanView does not carry (region, action, inspection type)
 * from the catalogs the app already uses. Names are decoration: each catalog is
 * emitted as soon as it arrives, the board never waits for them, and a failed
 * lookup degrades to "Unknown …" on the card.
 */
@Injectable({ providedIn: 'root' })
export class PlanNameCatalogService {
  private readonly regions = inject(RegionsService);
  private readonly actions = inject(ActionsService);
  private readonly crudBasicDomains = inject(CrudBasicDomainsService);

  /**
   * `api/Action?assetId=` is slow (ThreeD asset info over the bus, then every anomaly
   * action and repair of every customer, filtered in memory) and its answer barely
   * changes, so it is fetched once per asset for the session.
   */
  private readonly actionsByAsset = new Map<string, Observable<Names>>();

  /** Each emission carries one catalog; merge them into the previous state. */
  load(assetId: string, plans: readonly PlanView[]): Observable<Partial<NameCatalog>> {
    // Periodic identities are inspection types; an Action identity can be one too
    // (RBA "improve inspection"), so ask for every identity and let the lookup order decide.
    const interventionIds = [...new Set(plans.map(p => p.ExactInterventionIdentity).filter(Boolean))];

    const regions$ = this.regions.getRegions(assetId).pipe(
      map(list => toMap(list ?? [], r => r.id, r => r.name)),
      catchError(() => of(new Map<string, string>()))
    );

    // Plans created by RBA name themselves through their snapshot. Only migrated
    // Action Plans need the actions catalog, so skip the slow call when none exist.
    const actions$ = plans.some(needsActionCatalog) ? this.actionsFor(assetId) : of(new Map<string, string>());

    const inspectionTypes$ = interventionIds.length
      ? this.crudBasicDomains.getCrudBasicDomainsInspectionList(interventionIds).pipe(
          map(result => toMap(result?.listRows ?? [], t => t.id, t => t.name)),
          catchError(() => of(new Map<string, string>()))
        )
      : of(new Map<string, string>());

    return merge(
      regions$.pipe(map(regions => ({ regions }))),
      actions$.pipe(map(actions => ({ actions }))),
      inspectionTypes$.pipe(map(inspectionTypes => ({ inspectionTypes })))
    );
  }

  /** The actions registered for the asset, id to name; cached as above. */
  actionsFor(assetId: string): Observable<Names> {
    let cached = this.actionsByAsset.get(assetId);
    if (!cached) {
      cached = this.actions.getActions(assetId).pipe(
        map(list => toMap(list ?? [], a => a.Id, a => a.Name) as Names),
        // ActionsService swallows errors into []; do not keep an empty answer that
        // may only mean the call failed, so the next load asks again.
        tap(names => {
          if (names.size === 0) this.actionsByAsset.delete(assetId);
        }),
        catchError(() => {
          this.actionsByAsset.delete(assetId);
          return of(new Map<string, string>() as Names);
        }),
        shareReplay(1)
      );
      this.actionsByAsset.set(assetId, cached);
    }
    return cached;
  }
}

function toMap<T>(list: readonly T[], key: (x: T) => string | undefined, value: (x: T) => string | undefined) {
  const result = new Map<string, string>();
  for (const x of list) {
    const k = key(x);
    const v = value(x);
    if (k && v) result.set(k, v);
  }
  return result;
}
